/**
 * 退款页。
 *
 * 申请退款表单（金额/原因）+ 我的退款记录列表（状态）。
 *
 * @module pages/RefundPage
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';

import PageHeader from '@/components/common/PageHeader';
import MoneyText from '@/components/common/MoneyText';
import DataTable, { type Column } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useToast } from '@/hooks/useToast';
import { useApi } from '@/hooks/useApi';
import { createRefund, listMyRefunds } from '@/services/refund';
import type { RefundRequest, RefundStatus } from '@/types';
import dayjs from 'dayjs';

/** 退款状态元信息。 */
const REFUND_STATUS_META: Record<
  RefundStatus,
  { label: string; color: 'warning' | 'info' | 'error' | 'success' }
> = {
  pending: { label: '待处理', color: 'warning' },
  approved: { label: '已通过', color: 'info' },
  rejected: { label: '已拒绝', color: 'error' },
  completed: { label: '已完成', color: 'success' },
};

/**
 * 退款页组件。
 *
 * @returns 页面元素。
 */
export default function RefundPage(): JSX.Element {
  const { data, loading, refetch } = useApi(() => listMyRefunds());
  const { show, Toast } = useToast();

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      show('请输入有效退款金额', 'warning');
      return;
    }
    if (!reason.trim()) {
      show('请填写退款原因', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      await createRefund({ amount: value, reason: reason.trim() });
      show('退款申请已提交');
      setAmount('');
      setReason('');
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '提交失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: Column<RefundRequest>[] = [
    {
      key: 'amount',
      header: '金额',
      align: 'right',
      render: (r) => <MoneyText value={r.amount} />,
    },
    { key: 'reason', header: '原因' },
    {
      key: 'status',
      header: '状态',
      render: (r) => (
        <StatusChip
          label={REFUND_STATUS_META[r.status].label}
          color={REFUND_STATUS_META[r.status].color}
        />
      ),
    },
    {
      key: 'createdAt',
      header: '申请时间',
      render: (r) => dayjs(r.createdAt).format('YYYY-MM-DD HH:mm'),
    },
    {
      key: 'handledAt',
      header: '处理时间',
      render: (r) =>
        r.handledAt ? (
          dayjs(r.handledAt).format('YYYY-MM-DD HH:mm')
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
  ];

  return (
    <Box>
      <PageHeader title="退款" subtitle="申请余额退款并跟踪处理进度" />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1.4fr' },
          gap: 3,
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              <AssignmentReturnIcon
                fontSize="small"
                style={{ verticalAlign: 'middle', marginRight: 6 }}
              />
              申请退款
            </Typography>
            <TextField
              label="退款金额（元）"
              type="number"
              fullWidth
              margin="normal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <TextField
              label="退款原因"
              fullWidth
              margin="normal"
              multiline
              minRows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button
              variant="contained"
              fullWidth
              size="large"
              sx={{ mt: 2 }}
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? '提交中...' : '提交申请'}
            </Button>
          </CardContent>
        </Card>

        <Box>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            我的退款记录
          </Typography>
          <DataTable
            columns={columns}
            rows={data ?? []}
            rowKey={(r) => r.id}
            loading={loading}
            emptyText="暂无退款记录"
          />
        </Box>
      </Box>
      {Toast}
    </Box>
  );
}
